import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiX, FiUser, FiTruck, FiClipboard } from 'react-icons/fi';
import { searchService } from '../../services';
import { useTerms } from '../../utils/useTerms';
import './GlobalSearch.css';

function GlobalSearch() {
  const { t } = useTerms();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ workOrders: [], customers: [], vehicles: [] });
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  // Debounced search
  useEffect(() => {
    if (query.length < 2) {
      setResults({ workOrders: [], customers: [], vehicles: [] });
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await searchService.globalSearch(query, 5);
        setResults(response.data || { workOrders: [], customers: [], vehicles: [] });
      } catch (err) {
        console.error('Error en búsqueda:', err);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard shortcut (Ctrl+K or Cmd+K)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
      if (event.key === 'Escape') {
        setIsOpen(false);
        setFocused(false);
        inputRef.current?.blur();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleFocus = () => {
    setFocused(true);
    setIsOpen(true);
  };

  const handleClear = () => {
    setQuery('');
    setResults({ workOrders: [], customers: [], vehicles: [] });
    inputRef.current?.focus();
  };

  const handleResultClick = (type, id) => {
    setIsOpen(false);
    setQuery('');
    setResults({ workOrders: [], customers: [], vehicles: [] });

    switch (type) {
      case 'workOrder':
        navigate(`/workorders/${id}`);
        break;
      case 'customer':
        navigate(`/customers/${id}`);
        break;
      case 'vehicle':
        navigate(`/vehicles/${id}`);
        break;
      default:
        break;
    }
  };

  const hasResults =
    results.workOrders?.length > 0 ||
    results.customers?.length > 0 ||
    results.vehicles?.length > 0;

  const showDropdown = isOpen && query.length >= 2;

  return (
    <div className={`global-search-globalsearch ${focused ? 'focused-globalsearch' : ''}`} ref={containerRef}>
      <div className="input-wrapper-globalsearch">
        <FiSearch className="search-icon-globalsearch" />
        <input
          ref={inputRef}
          type="text"
          placeholder="Buscar..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={handleFocus}
          className="search-input-globalsearch"
        />
        {query && (
          <button className="clear-button-globalsearch" onClick={handleClear}>
            <FiX />
          </button>
        )}
        <span className="shortcut-globalsearch">Ctrl+K</span>
      </div>

      {showDropdown && (
        <div className="dropdown-globalsearch">
          {loading && (
            <div className="loading-globalsearch">Buscando...</div>
          )}

          {!loading && !hasResults && query.length >= 2 && (
            <div className="no-results-globalsearch">No se encontraron resultados</div>
          )}

          {!loading && hasResults && (
            <>
              {results.workOrders?.length > 0 && (
                <div className="section-globalsearch">
                  <div className="section-header-globalsearch">
                    <FiClipboard /> Órdenes de Trabajo
                  </div>
                  {results.workOrders.map((order) => (
                    <div
                      key={order._id}
                      className="result-item-globalsearch"
                      onClick={() => handleResultClick('workOrder', order._id)}
                    >
                      <span className="result-title-globalsearch">{order.orderNumber}</span>
                      <span className="result-subtitle-globalsearch">
                        {order.customer?.firstName} {order.customer?.lastName} - {order.vehicle?.licensePlate}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {results.customers?.length > 0 && (
                <div className="section-globalsearch">
                  <div className="section-header-globalsearch">
                    <FiUser /> Clientes
                  </div>
                  {results.customers.map((customer) => (
                    <div
                      key={customer._id}
                      className="result-item-globalsearch"
                      onClick={() => handleResultClick('customer', customer._id)}
                    >
                      <span className="result-title-globalsearch">
                        {customer.firstName} {customer.lastName}
                      </span>
                      <span className="result-subtitle-globalsearch">{customer.phone}</span>
                    </div>
                  ))}
                </div>
              )}

              {results.vehicles?.length > 0 && (
                <div className="section-globalsearch">
                  <div className="section-header-globalsearch">
                    <FiTruck /> {t('plural')}
                  </div>
                  {results.vehicles.map((vehicle) => (
                    <div
                      key={vehicle._id}
                      className="result-item-globalsearch"
                      onClick={() => handleResultClick('vehicle', vehicle._id)}
                    >
                      <span className="result-title-globalsearch">
                        {vehicle.brand} {vehicle.model} ({vehicle.year})
                      </span>
                      <span className="result-subtitle-globalsearch">{vehicle.licensePlate}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default GlobalSearch;
